import _ from 'lodash'
import $ from 'jquery'
import { Borders, FONT_SIZE } from './excel-workbook'
import dataTypeResolver from 'scoreboard/components/data-type-resolver'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER, PERCENT, CURRENCY, YES_NO } = DataType

const DEFAULT_RAW_VALUE_PROPERTY = 'value'
const IS_NOT_DISPLAYED = 'is-not-displayed'
/**
 * Reads table data and metadata (formatting) from a JQuery table body object
 * and optionally aggregates it with any raw data provided by the model
 *
 * @see excel-export.js#saveTableAsFile for documentation
 */
var HtmlTableParser = {

	/**
	 * Get cell metadata from the DOM for creating an excel table
	 *
	 * @param {jQuery} tableContext.bodyEl <tbody> jquery object
	 * @param {Number|String} [tableContext.id=0]
	 * @param {ExcelExportOptions} opts
	 *
	 * @returns two dimensional object array of objects used to construct an Excel Sheet
	 */
	parse: function(tableContext, opts) {
		const {bodyEl} = tableContext
		/*
		Not deep, don't want the model cloned (to preserve memory)
		The model should be treated as immutable; we assign everything required
		from the model onto the cell context (_enrichCellObjectWithRawData)
		 */
		var opts = _.clone(opts)
		opts.canvas = document.createElement('canvas')
		opts.canvas2dContext = opts.canvas.getContext('2d')
		var columnHeadings = opts.columnHeadings || bodyEl.parent().find('th')
		var headerContext = this._createHeaderContext(columnHeadings, opts)
		opts.headerContext = headerContext
		var columnCount = headerContext.columnCount
		var tableRows = bodyEl.parent().find('tr').not(`.${IS_NOT_DISPLAYED}`)
		return this._tranformHTMLRowstoSheetData(tableContext, tableRows, opts, columnCount)
	},

	_createHeaderContext: function (columnHeadings, opts) {
		var context = { dataIds: [], valueAttributes: [], valuePositions:[] }
		var firstRow, lastRow

		_.reduce(columnHeadings, function (colIndex, thEl, i) {
			var headerCell = $(thEl)
			var row = headerCell.parent()

			if (!firstRow) {
				firstRow = row
			}

			// Could have <th> from more than one row, and all of their attributes
			// should be merged together, so reset the index each time we hit a new row
			var sameRow = row.is(lastRow)
			if (!sameRow) {
				colIndex = 0
				lastRow = row
			}
			return colIndex + this._readHeaderAttributes(headerCell, opts, context, colIndex)
		}.bind(this), 0)

		// The new report added rowspans in the header; it's simpler to just
		// calculate the column count by looking only at the first header row.
		context.columnCount = _.sumBy(firstRow.children(), c => {
			return $(c).hasClass(IS_NOT_DISPLAYED) ? 0 : c.colSpan || 1
		})

		return context
	},

	/**
	 * This is where the data- attributes are set from
	 * potentially multiple source elements (<th>)
	 *
	 * @param th JQuery element
	 * @param {object} opts
	 * @param {object} context
	 * @param {number} colIndex
	 *
	 * @return {number} the number of columns based on the colspan of <th>
	 * @private
	 */
	_readHeaderAttributes: function(th, opts, context, colIndex) {

		if (th.hasClass(IS_NOT_DISPLAYED)) {
			return 0
		}

		var cs = th.attr('colspan')
		var newColumns = cs ? _.toNumber(cs) : 1
		var isFirstRow = context.dataIds.length < colIndex + 1
		var shouldAddHeader = isFirstRow

		var setValues = (dataAttr, ctxProperty, useIndicies) => {
			const attributeValue = dataAttr && th.attr(dataAttr)
			if (shouldAddHeader) {
				_.times(newColumns, function(offset) {
					if (attributeValue) {
						ctxProperty.push(attributeValue)
					} else if (opts.indexBasedIdsEnabled && useIndicies) {
						ctxProperty.push(colIndex + offset)
					} else {
						ctxProperty.push(undefined)
					}
				})
			} else if (dataAttr) {
				ctxProperty[colIndex] = _.defaultTo(attributeValue, ctxProperty[colIndex])
			}
		}

		setValues(_.get(opts,'dataAttributes.th'), context.dataIds, true)
		setValues(_.get(opts,'dataAttributes.value'), context.valueAttributes, true)
		setValues(_.get(opts,'dataAttributes.valuePosition'), context.valuePositions, false)

		return newColumns
	},

	_tranformHTMLRowstoSheetData: function (tableContext, tableRows, opts, columnCount) {
		var rowCount = tableRows.length
		var cellMetaDataInRows = this._parseTableRows(tableContext, rowCount, tableRows, opts)
		var sheetData = this._buildSheetRows(rowCount, columnCount, cellMetaDataInRows, opts)
		return sheetData
	},

	/**
	 * Iterate through entire table and extract data and styles, easier to work
	 * with and more lightweight than a big JQuery Object Model
	 *
	 * @param {Object} tableContext
	 * @param {Number} rowCount
	 * @param {JQuery[]} tableRows tr objects
	 * @param {Object} opts
	 * @param {Array} [opts.dataAttributes] 'data-' attributes in the DOM
	 *
	 * @return {Array} of objects with properties that we need to generate XLSX
	 * @private
	 */
	_parseTableRows: function (tableContext, rowCount, tableRows, opts) {
		var attributes = opts.dataAttributes || {}
		var rows = _.times(rowCount, function () {
			return []
		})
		var self = this //jquery find() uses 'this' for the element, we cant' bind here
		tableRows.each(function (rowIndex) {
			var tr = $(this)
			var rowCells = rows[rowIndex]
			// If not index based, rowsIds should be copied down with rowspans
			var missingRowIdValue = opts.indexBasedIdsEnabled ? rowIndex : undefined
			var modelRowId = (attributes.tr && tr.attr(attributes.tr)) || missingRowIdValue
			// #54507 for performance reasons, try not to use CSS selectors here
			tr.children().each((colIndex, el) => {
				if (el.classList.contains(IS_NOT_DISPLAYED)) return

				var td = $(el)
				var a = td.find('a')
				var valueElement = a.length > 0 ? a.first() : td
				var cellData = self._createCellData(rowIndex, tableContext, modelRowId, valueElement, td, opts)
				cellData.colIndex = colIndex
				rowCells.push(cellData)
				self._setRowId(cellData, rowCells, rows[rowIndex-1])
			})
		})

		return rows
	},

	/**
	 * Reads a HTML element and creates an in-memory representation of the
	 * attributes required for export.
	 *
	 * @param {Number} rowIndex
	 * @param tableContext.id The id of the table for modelLookup invocation
	 * @param {string} modelRowId the row identifier for the model, as provided by dataAttributes.tr
	 * @param {jQuery} valueElement the element that contains the value (could be a td
	 *     or a) some style attributes (i.e. bold) are only available on this
	 *     element
	 * @param {jQuery} cellElement the TD or TH element, could be  valueElement
	 * used for cell level attributes (i.e. colspan, etc)
	 * @param opts see excel-export.js#saveTableAsFile
	 */
	_createCellData: function (rowIndex, tableContext, modelRowId, valueElement, cellElement, opts) {
		var header = cellElement.prop('tagName').toUpperCase() === 'TH'
		var domValue = this._extractTextValueFromDOMElement(cellElement)
		var css = cellElement.attr('class') || ''
		var colorRex = /.*color ([\w-]*).*/.exec(css)
		var color = colorRex && colorRex[1]
		var bgColor = header ? 'gray' : color
		var total = !_.isNull(css.match(/.*total[\s$]/))

		const spanNumber = attrName => {
			var spanValue = cellElement.attr(attrName)
			return spanValue ? _.toNumber(spanValue) : 1
		}
		const rowspan = spanNumber('rowspan')
		const colspan =  spanNumber('colspan')
		var rawValueProperty = this._getRawValueProperty(cellElement, opts, rowspan, colspan)

		// A caller can provide its own lookup function to return the model
		// representation of a cell (i.e. dataset aggregation tables)
		const args = [tableContext.id, rowIndex, cellElement, modelRowId]
		const model = _.invoke(opts, 'dataAttributes.modelLookup', ...args)
		const rowId = _.isUndefined(modelRowId) ? _.get(model, 'rowId') : modelRowId

		// Assign the correct font to the (shared) canvas so we get the correct pixel width
		const canvasCtx = opts.canvas2dContext || {}
		const bold = valueElement.css('font-weight') > 400 ? 'bold' : ''
		canvasCtx.font = `${bold} ${FONT_SIZE}px ${valueElement.css('font-family')}`

		var cellData = {
			modelLookup: !!model, // If a cell level model was provided via the lookup function
			model,
			rowId,
			header,
			value: domValue,
			domValue,
			rawValueProperty,
			valueType: cellElement.data('value-type'),
			rowspan,
			colspan,
			bgColor,
			total,
			horizontalAlign: valueElement.css('text-align'),
			verticalAlign: cellElement.css('vertical-align'),
			width: cellElement.outerWidth(),
			textWidth: canvasCtx.measureText && Math.ceil(canvasCtx.measureText(domValue).width),
			height: cellElement.outerHeight(),
			fontWeight: valueElement.css('font-weight')
		}
		this._createBorder(cellData)
		return cellData
	},

	_extractTextValueFromDOMElement: function (cellElement) {
		var domValue = _.trim(cellElement.text())
		var divs = cellElement.find('div')

		var normalize = function(value) {
			return value.replace(/[\n]/g, ' ') // convert new lines to spaces
						.replace(/\s+/g, ' ')  // one space between words
		}

		if (divs.length > 0) {
			// Divs mean there are line breaks, and they should be preserved in the export
			var items = []
			const pushText = function (el) {
				// #32177 We trim here because extra white space can cause formatting issues
				// If an export requires significant whitespace it can override this by
				// providing it's own modelLookup
				// (e.g. datasets-overview-export does this for indentation)
				items.push(_.trim(normalize($(el).text())))
			}
			const principals = cellElement.find('.principal-container')
			if (principals.length > 0) {
				// #32177 Avatars are an outlier; must only include the principal label once
				cellElement.find('.principal-container .label')
					.each(function () { pushText(this) })
			} else {
				// #50991 Recursively drill down through all divs, in case they
				// are nested
				function processElement (el) {
					if (el.nodeType == Node.TEXT_NODE) {
						// A plain text node with non-whitespace should be added
						if (el.textContent.trim().length > 0) {
							pushText(el)
						}
					} else if (el.nodeType == Node.ELEMENT_NODE) {
						if (el.tagName == 'DIV' || el.tagName == 'TD') {
							// A div (or the root table cell) could contain
							// nested divs, so process each child individually
							el.childNodes.forEach(processElement)
						} else {
							// If it's not a div, add the text content
							pushText(el)
						}
					}
				}

				processElement(cellElement[0])
			}
			domValue = _.join(items, '\n')
		} else {
			// Otherwise, it's just DOM whitespace that should be normalized
			domValue = normalize(domValue)
		}
		return domValue
	},

	_createBorder(cellData) {
		var b = {}
		const rowspan = _.get(cellData, 'rowspan', 1)

		b.top = b.bottom = Borders.textCell
		if (cellData.header) {
			b.top = Borders.headerCell
		} else {
			b.left = b.right = Borders.textCell
			if (cellData.bgColor) {
				b.top = b.bottom = Borders.coloredCell
				b.left = b.right = undefined
			}
			// total column borders
			if (cellData.total) {
				b.left = b.right = Borders.totalVertical
			}
		}
		cellData.border = b
		return cellData.border
	},

	/**
	 * Returns the name of the model property to be used to assign the cell value,
	 * or null if no rawValueProperty should be applied to the cell.
	 */
	_getRawValueProperty: function (cellElement, opts, rowspan, colspan) {

		// If a raw value property is being read from the row here, it should not
		// be applied if the cell has a span because it can't possibly be the right
		// cell for the 'data-value' attribute on the row

		// #25521 report notes were picking up the row's rvp,
		// so colspan was added retroactively
		const singleCell = rowspan < 2 && colspan < 2
		if (!singleCell) {
			return null // null, not undefined; it indicates we don't want a rvp applied
		}

		var tr = cellElement.parent()
		var attributes = _.get(opts, 'dataAttributes', {})
		var attr = tr && attributes.value && tr.attr(attributes.value)
		return _.defaultTo(attr, DEFAULT_RAW_VALUE_PROPERTY)
	},

	/**
	 * Assign the id of the previous cell or row if one was not set
	 * @param cellData the current cell
	 * @param {array} rowCells all cells added to the current row
	 * @param previousRow
	 * @private
	 */
	_setRowId: function (cellData, rowCells, previousRow) {
		if (_.isUndefined(cellData.rowId)) {
			var previousCell = rowCells[rowCells.length-2]
			var previousRowFirstCell = previousRow && previousRow[0]
			if (previousCell && previousCell.rowId) {
				cellData.rowId = previousCell.rowId
			} else if (previousRowFirstCell) {
				cellData.rowId = previousRowFirstCell.rowId
			}
		}
	},

	/**
	 * Use the table data to build out a complete grid
	 *
	 * @param rowCount
	 * @param columnCount
	 * @param {array} cellMetaDataInRows the cell data for all rows (two dimensional array)
	 * @param opts
	 * @return {Array}
	 * @private
	 */
	_buildSheetRows: function (rowCount, columnCount, cellMetaDataInRows, opts) {
		var rows = _.times(rowCount, function () {
			return []
		})
		var valueAttributes = _.result(opts.headerContext, 'valueAttributes')

		// Can be set on a header when a table has multiple columns per (scorecard) item
		// (i.e. Report has (score,value,thresholds,etc) by period)
		// A raw value defined on the row must take precedence over the TH attribute (i.e. thresholds)
		// If it was set on the row, it will have already been assigned
		var rawValuePropertyDefined = (cellObject) => {
			const p = cellObject.rawValueProperty
			return !_.isUndefined(p) && (_.isNull(p) || p !== DEFAULT_RAW_VALUE_PROPERTY)
		}

		// We go down columns instead of across rows to make filling in cells
		// for rowpans easier
		for (var c = 0; c < columnCount; c++) {
			var valueAttr = _.nth(valueAttributes,c)
			for (var r = 0; r < rowCount; r++) {
				var cells = cellMetaDataInRows[r]
				var currRow = rows[r]
				var valuesInRow = _.size(currRow)
				// Skip cells if we don't need a value due to colspans
				var needValue = !(valuesInRow >= c + 1)
				var cellObject = needValue && cells.shift()
				// account for cells that span the rest of the columns
				if (cellObject === undefined) {
					currRow.push({})
				} else {
					if (cellObject && !rawValuePropertyDefined(cellObject) && valueAttr) {
						cellObject.rawValueProperty = valueAttr
					}
					var skipThisManyRows = this._addCellToRow(cellObject, rows, r, c, opts)
					r += skipThisManyRows
				}
			}
		}

		this._adjustDataValues(rows, opts)
		return rows
	},

	/**
	 * Restore any DOM values based on 'data-value-position' properties
	 *
	 * This logic may break if the header structure changes, but it works for
	 * the only place it's needed right now, which is report thresholds.
	 *
	 * @param {Array[][]} rows of cellObjects
	 * @param opts.headerContext
	 */
	_adjustDataValues(rows, opts) {
		for (var r = 0; r < rows.length; r++) {
			for (var c = 0; c < rows[r].length; c++) {
				const positions = _.get(opts, 'headerContext.valuePositions')
				if (positions) {
					const vpos = positions[c]
					/*
					 * The scenario is: a set of key/value pairs (thresholds) span n number of rows,
					 * where the raw threshold value must be sourced from the model.
					 *
					 * A rawValueProperty is set on the row to indicate which threshold value to use,
					 * but that value must only be applied to the column with the value and not the label.
					 */
					if (vpos > 0) {
						//This is a simple check just because thresholds only have two positions,
						//and the first one is the label, so we can just set that and be done.
						const cell = rows[r][c]
						cell.value = cell.domValue
					}
				}
			}
		}
	},


	/**
	 *
	 * @param cellObject
	 * @param rows
	 * @param rowIndex
	 * @param colIndex
	 * @param opts
	 * @return {number} rowspan of the cell
	 * @private
	 */
	_addCellToRow: function (cellObject, rows, rowIndex, colIndex, opts) {
		var rowIncrement = 0
		if (cellObject) {
			this._enrichCellObjectWithRawData(cellObject, colIndex, opts)
			rows[rowIndex].push(cellObject)

			const cloneStyles = cellObject => {
				return {
					// Carry along style attributes that apply to the merge
					header: cellObject.header,
					border: cellObject.border
				}
			}

			if (cellObject.rowspan) {
				// Fill cells below with cell objects
				for (var i = 1; i < cellObject.rowspan; i++) {
					var colCount = cellObject.colspan || 1
					_.times(colCount, function() { rows[rowIndex + i].push(cloneStyles(cellObject)) })
				}
				rowIncrement = cellObject.rowspan - 1 // we've filled the data, now advance
			}

			// fill in colspan cells
			for (var i = 1; i < cellObject.colspan; i++) {
				rows[rowIndex].push(cloneStyles(cellObject))
			}
		}
		return rowIncrement
	},

	/**
	 * If the table has formatted values (currency, percents, etc)
	 * we need to use the raw data for excel.  This function uses the options
	 * provided to replace the DOM value with the raw value.
	 *
	 * @param cellObject The current cell
	 * @param colIndex 0 based
	 * @param opts.headerContext.data Used to get the thAttribute for colIndex
	 * @param opts.model Used to lookup the raw data object in the SB API Model
	 * based on the rowIndex and derived columnDataId
	 *
	 * @private
	 */
	_enrichCellObjectWithRawData: function (cellObject, colIndex, opts) {
		var rowDataId = cellObject.rowId
		const hasModel = cellObject.model || (opts.model && rowDataId != undefined)

		// If the cell is text based we must leave the value == domValue
		if (cellObject.valueType == 'text') {
			return
		}

		if (hasModel) {
			// cell level must take precedence if both are provided
			const model = cellObject.model || opts.model
			// #12292 merged cells are associated to the rightmost header
			var offsetIndex = colIndex
			if (cellObject.horizontalAlign === 'right') {
				offsetIndex += (_.get(cellObject, 'colspan', 1) - 1)
			}

			/**
			 * The model object for the active row (i.e. scorecard item)
			 * If a modelLookup was provided (dataset aggregations) then the model
			 * will be set on each cellObject, and there are no row level models
			 * All formatting metadata must be provided by the lookup
			 */
			var modelRowObj = model[rowDataId] || {}
			var dataType = modelRowObj && _.result(modelRowObj, 'dataType', NUMBER)
			cellObject.precision = _.get(modelRowObj, 'precision')
			cellObject.currencyCode = _.get(modelRowObj, 'currencyCode')

			this._setRawDataObj(cellObject, modelRowObj, offsetIndex, dataType, opts)
			this._overrideProperties(cellObject, dataType)

			let {rawValueProperty, rawDataObj} = cellObject
			if (rawDataObj && rawValueProperty !== undefined) {
				dataType = _.result(rawDataObj, 'dataType', dataType)
				// Must not be set for string values (absence of rawValueProperty)
				// because excel formatting does special things with numbers and currencies
				cellObject.dataType = dataTypeResolver(dataType, rawValueProperty, cellObject.value)
				const rawDataTypes = [PERCENT, NUMBER, CURRENCY]
				if (_.includes(rawDataTypes, cellObject.dataType)) {
					if (opts.indexBasedIdsEnabled) {
						// This supports dataset-results, and any export with a raw 'value' property in the model
						this._assignValue(cellObject,
							_.get(rawDataObj, 'value', cellObject.value))

					} else {
						this._assignValue(cellObject,
							_.get(rawDataObj, rawValueProperty, cellObject.value))
					}
				}
			}

			// This is special, it should use the global config, and not the metric precision
			if (rawValueProperty === 'impact') {
				delete cellObject.precision
			}
		}
	},

	_setRawDataObj(cellObject, modelRowObj, colIndex, dataType, opts) {
		if (opts.indexBasedIdsEnabled) {
			cellObject.rawValueProperty = colIndex
			cellObject.rawDataObj = modelRowObj[colIndex]
		} else if (cellObject.model) {
			const model = cellObject.model
			cellObject.rawDataObj = model
			cellObject.rawValueProperty = DEFAULT_RAW_VALUE_PROPERTY
			cellObject.dataType = model.dataType
		} else {
			/**
			 * Periodic attributes will have a period-id associated to their column,
			 * but non-periodic attributes also need to be formatted correctly since
			 * they are not all textual
			 */
			const columnDataId = opts.headerContext.dataIds[colIndex]
			const colDataAttr = opts.headerContext.valueAttributes[colIndex]

			// If there is a data attribute in the column it must take precedence
			// over the row's data attribute (24065, 24064)
			cellObject.rawValueProperty = colDataAttr || cellObject.rawValueProperty

			if (columnDataId) {
				cellObject.rawDataObj = _.get(modelRowObj, columnDataId)
				cellObject.columnDataActive = true
			} else {
				cellObject.rawValueProperty = colDataAttr
				cellObject.rawDataObj = modelRowObj
			}
		}
	},

	/**
	 * Sets the following properties based on the precedence inherent to the model
	 * or any specific business logic
	 * 	- precision
	 * 	- value
	 */
	_overrideProperties(cellObject, dataType) {
		const rawData = cellObject.rawDataObj
		const rvp = cellObject.rawValueProperty
		// This means a rawDataObject was taken from the model keyed off of the table column 'data-' attribute
		const hadDataFromColumnDataAttribute = rawData && cellObject.columnDataActive

		if (hadDataFromColumnDataAttribute) {
			cellObject.precision = _.defaultTo(rawData.precision, cellObject.precision)
			if (rvp && dataType.toLowerCase() !== YES_NO) {
				this._assignValue(cellObject, _.get(rawData, rvp, cellObject.value))
			}
		} else {
			// Don't override the row level precision with top-level model precision
			cellObject.precision = _.defaultTo(cellObject.precision, _.get(rawData, 'precision'))
		}

		// No more business rules in the parser, from now on exports must provide
		// their own model with the expected value/dataType.  This model takes precedence
		// over any existing rules that may have been applied.
		if (cellObject.modelLookup && _.toLower(cellObject.dataType) !== YES_NO) {
			// cellObject.value is the domValue unless it is replaced with a raw value here
			this._assignValue(cellObject, _.get(rawData, rvp, cellObject.value))
		}
	},

	/**
	 * Centralizes rules for assigning values to a cell.
	 *
	 * Squashes (24021) once and for all.  Never put a value in an export that is
	 * not present in the DOM.
	 */
	_assignValue(cellObject, value) {
		if (_.isNil(value)) return

		if (!_.isEmpty(_.trim(cellObject.domValue))) {
			cellObject.value = value
		}
	}

}
export default HtmlTableParser

HtmlTableParser.__filename__ = 'components/export/html-table-parser.js'
